import React, { useState, useEffect, useCallback } from 'react';
import api from '../../api';
import LoadingSpinner from '../../components/LoadingSpinner';
import BusStopForm from './BusStopForm';
import BusStopTable from './BusStopTable';

const EMPTY_FORM = {
  title: '',
  arrival_time: '',
  address: '',
  latitude: '',
  longitude: '',
  is_active: true,
};

const BusStop = () => {
  const [busStops, setBusStops] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [successMsg, setSuccessMsg] = useState('');
  const [formData, setFormData] = useState({ ...EMPTY_FORM });
  const [activeBusStop, setActiveBusStop] = useState(null);

  const showSuccess = (msg) => {
    setSuccessMsg(msg);
    setTimeout(() => setSuccessMsg(''), 3000);
  };

  const showError = (msg) => {
    setError(msg);
    setTimeout(() => setError(''), 4000);
  };

  const fetchBusStops = useCallback(async () => {
    setLoading(true);
    try {
      const response = await api.get('/api/v1/bus_stops');
      const stops = response.data;
      setBusStops(stops);
      setActiveBusStop(stops.find((s) => s.is_active) || null);
    } catch (err) {
      showError('Error al cargar las ubicaciones de micro.');
    } finally {
      setLoading(false);
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    fetchBusStops();
  }, [fetchBusStops]);

  const handleInputChange = (event) => {
    const value = event.target.type === 'checkbox' ? event.target.checked : event.target.value;
    setFormData((prev) => ({
      ...prev,
      [event.target.name]: value,
    }));
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    try {
      await api.post('/api/v1/bus_stop', formData);
      showSuccess('Ubicación de micro creada correctamente.');
      setFormData({ ...EMPTY_FORM });
      fetchBusStops();
    } catch (err) {
      showError('Error al crear la ubicación. Verificá los datos e intentá de nuevo.');
    }
  };

  const handleToggle = async (id) => {
    try {
      await api.put(`/api/v1/bus_stop/${id}/toggle`);
      fetchBusStops();
    } catch (err) {
      showError('Error al cambiar el estado de la ubicación.');
    }
  };

  const handleDelete = async (id) => {
    try {
      await api.delete(`/api/v1/bus_stop/${id}`);
      showSuccess('Ubicación eliminada correctamente.');
      fetchBusStops();
    } catch (err) {
      showError('Error al eliminar la ubicación.');
    }
  };

  return (
    <div className="container" style={{ paddingTop: '24px' }}>

      {/* Feedback messages */}
      {error && (
        <div className="alert alert-danger" style={{ borderRadius: '6px' }}>{error}</div>
      )}
      {successMsg && (
        <div className="alert alert-success" style={{ borderRadius: '6px' }}>{successMsg}</div>
      )}

      {/* Section 1 — Form + active status */}
      <div style={{ marginBottom: '40px' }}>
        <h4 style={{ marginBottom: '16px' }}>Gestión de Micro</h4>

        {/* Active bus stop status */}
        <div
          style={{
            backgroundColor: activeBusStop ? '#fff8f8' : '#f8f9fa',
            border: `1px solid ${activeBusStop ? '#C73B33' : '#dee2e6'}`,
            borderRadius: '8px',
            padding: '16px 20px',
            marginBottom: '24px',
            display: 'flex',
            alignItems: 'center',
            gap: '12px',
          }}
        >
          <span style={{ fontSize: '1.4rem' }}>🚌</span>
          <div>
            {activeBusStop ? (
              <>
                <strong style={{ color: '#C73B33' }}>Micro activo:</strong>{' '}
                {activeBusStop.title}
                {activeBusStop.address && (
                  <span style={{ color: '#666', fontSize: '0.9rem' }}>
                    {' '}— {activeBusStop.address}
                  </span>
                )}
                {activeBusStop.arrival_time && (
                  <span style={{ color: '#666', fontSize: '0.9rem' }}>
                    {' '}| Llegada: {activeBusStop.arrival_time}
                  </span>
                )}
              </>
            ) : (
              <span style={{ color: '#888' }}>No hay ningún micro activo en este momento.</span>
            )}
          </div>
        </div>

        <BusStopForm
          formData={formData}
          onInputChange={handleInputChange}
          onSubmit={handleSubmit}
        />
      </div>

      <div style={{ height: '40px' }}></div>

      {/* Section 2 — Historic table */}
      <div>
        <h4 style={{ marginBottom: '16px' }}>Histórico de Viajes</h4>
        {loading ? (
          <LoadingSpinner />
        ) : (
          <BusStopTable
            busStops={busStops}
            onToggle={handleToggle}
            onDelete={handleDelete}
          />
        )}
      </div>

    </div>
  );
};

export default BusStop;
